import { DEFAULT_FLASH_TAX_FEE_PRODUCT_TYPE } from './taxRules';
import type { TaxFeeFormItem } from './taxRules';

export type SavedTaxFeeItem = {
  additional_tax?: boolean;
  currency?: string | null;
  exclude_flash_pay_at_property?: boolean;
  exclude_from_cm_rate?: boolean;
  name?: string | null;
  payable_at_property?: boolean;
  per_person?: boolean;
  product_type?: string | null;
  type?: string | null;
  unit?: string | null;
  value?: number | null;
};

export type TaxSettings = {
  all_taxes_and_fees_as_non_commssionable: boolean;
  channel_manager_reservation_rate: string;
  commission_statement: boolean;
  exclude_flash_pay_at_property: boolean;
  gstin: string;
  include_tax_amount_into_before_amount?: boolean;
  send_daily_override_rates: boolean;
  taxes_payable_at_property: boolean;
};

export type SavedPropertyTaxes = Partial<TaxSettings> & {
  channel_manager?: string | null;
  location_heading?: string | null;
  location_subheading?: string | null;
  taxes_and_fees?: Array<SavedTaxFeeItem> | null;
};

export type TaxFeesFormData = TaxSettings & {
  taxes_and_fees: Array<TaxFeeFormItem>;
};

export type TaxesAndFeesUpdatePayload = {
  all_taxes_and_fees_as_non_commssionable: boolean;
  channel_manager_reservation_rate: string;
  commission_statement: boolean;
  exclude_flash_pay_at_property: boolean;
  gstin: string;
  include_tax_amount_into_before_amount?: boolean;
  send_daily_override_rates: boolean;
  taxes_and_fees: Array<TaxFeeFormItem>;
  taxes_payable_at_property: boolean;
};

export const uiSchema = {
  gstin: {
    'ui:placeholder': 'Enter GSTIN',
    classNames: 'gstin-block',
  },
  commission_statement: {
    classNames: 'gstin-block',
  },
  taxes_and_fees: {
    items: {
      classNames: 'tax-block',
    },
  },
};

export const mapSavedTaxFee = (item: SavedTaxFeeItem): TaxFeeFormItem => ({
  additional_tax: !!item.additional_tax,
  exclude_flash_pay_at_property: !!item.exclude_flash_pay_at_property,
  exclude_from_cm_rate: !!item.exclude_from_cm_rate,
  name: item.name ?? '',
  payable_at_property: !!item.payable_at_property,
  per_person: !!item.per_person,
  product_type: item.product_type ?? DEFAULT_FLASH_TAX_FEE_PRODUCT_TYPE,
  type: item.type ?? 'night',
  unit:
    item.unit === 'amount'
      ? {
          amount: item.value ?? 0,
          currency: item.currency ?? '',
        }
      : {
          value: item.value ?? 0,
        },
});

export const buildTaxesAndFeesUpdatePayload = (formData: TaxFeesFormData): TaxesAndFeesUpdatePayload => ({
  // RJSF omits an emptied array from formData; without the fallback the backend
  // sees no key and keeps the existing taxes instead of clearing them.
  taxes_and_fees: formData.taxes_and_fees ?? [],
  commission_statement: formData.commission_statement,
  taxes_payable_at_property: formData.taxes_payable_at_property,
  all_taxes_and_fees_as_non_commssionable: formData.all_taxes_and_fees_as_non_commssionable,
  include_tax_amount_into_before_amount: formData.include_tax_amount_into_before_amount,
  gstin: formData.commission_statement ? formData.gstin : '',
  exclude_flash_pay_at_property: formData.exclude_flash_pay_at_property,
  channel_manager_reservation_rate: formData.channel_manager_reservation_rate,
  send_daily_override_rates: formData.send_daily_override_rates,
});

export const mapTaxSettings = (property: SavedPropertyTaxes): TaxSettings => ({
  all_taxes_and_fees_as_non_commssionable: !!property.all_taxes_and_fees_as_non_commssionable,
  channel_manager_reservation_rate: property.channel_manager_reservation_rate ?? 'default',
  commission_statement: !!property.commission_statement,
  exclude_flash_pay_at_property: !!property.exclude_flash_pay_at_property,
  gstin: property.gstin ?? '',
  include_tax_amount_into_before_amount: property.include_tax_amount_into_before_amount,
  send_daily_override_rates: !!property.send_daily_override_rates,
  taxes_payable_at_property: !!property.taxes_payable_at_property,
});
